import React, { useState, useEffect, useRef, useMemo } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useBrand } from '../context/BrandContext'
import { colors, radius, s, ui, chartPalette, tone, chartBase, lineGradient } from '../styles/theme'
import Chart from '../components/Chart'
import { useIsMobile } from '../hooks/useViewport'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

const style = {
  card: { ...s.cardSm, padding: '20px' },
  label: { ...s.label, marginBottom: '6px' },
  subval: { fontSize: '13px', color: colors.textSecondary, marginTop: '4px' }
}

const chipDanger = { ...ui.chip, background: s.alertDanger.background, border: s.alertDanger.border, color: s.alertDanger.color }

function gauge(pct) {
  const r = 36, stroke = 8
  const circ = 2 * Math.PI * r
  const offset = circ - (pct / 100) * circ
  return (
    <svg width="88" height="88" viewBox="0 0 88 88">
      <circle cx="44" cy="44" r={r} fill="none" stroke={colors.borderLight} strokeWidth={stroke} />
      <circle cx="44" cy="44" r={r} fill="none" stroke={colors.primary} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 44 44)"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="44" y="48" textAnchor="middle" fontSize="15" fontWeight="600" fill={colors.textPrimary}>{pct}%</text>
    </svg>
  )
}

function StatusDot({ active, label }) {
  return (
    <span style={ui.status(active)}>
      <span style={ui.statusDot(active)} />
      {label}
    </span>
  )
}

function BrandSyncRow({ b, headers, onRefresh }) {
  const [syncing, setSyncing] = useState(false)
  const [syncingCat, setSyncingCat] = useState(false)
  const [msg, setMsg] = useState('')

  async function syncProducts() {
    setSyncing(true); setMsg('')
    try {
      await axios.post(`${API}/admin/brands/${b.id}/sync`, {}, { headers })
      setMsg('Ürünler sync edildi ✓'); onRefresh()
    } catch (e) { setMsg('Hata: ' + (e.response?.data?.error || e.message)) }
    setSyncing(false)
  }

  async function syncCategories() {
    setSyncingCat(true); setMsg('')
    try {
      await axios.post(`${API}/admin/brands/${b.id}/sync-categories`, {}, { headers })
      setMsg('Kategoriler sync edildi ✓'); onRefresh()
    } catch (e) { setMsg('Hata: ' + (e.response?.data?.error || e.message)) }
    setSyncingCat(false)
  }

  return (
    <div style={{ background: colors.bgPage, borderRadius: radius.md, padding: '12px 14px', border: `1px solid ${colors.borderLight}` }}>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>{b.name}</span>
          <StatusDot active={b.is_active} label={b.is_active ? 'Aktif' : 'Pasif'} />
        </div>
        <div className="sx-wrap" style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {b.event_live && <span style={ui.chip}>● Canlı</span>}
          {b.smartbot_enabled && <span style={ui.chip}>Bot Açık</span>}
          <button onClick={syncProducts} disabled={syncing} style={{ ...ui.btnSm, opacity: syncing ? 0.7 : 1 }}>
            {syncing ? '...' : 'Ürün Sync'}
          </button>
          <button onClick={syncCategories} disabled={syncingCat} style={{ ...ui.btnSm, opacity: syncingCat ? 0.7 : 1 }}>
            {syncingCat ? '...' : 'Kategori Sync'}
          </button>
        </div>
      </div>
      <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontSize: '12px' }}>
        {[
          { val: b.product_count.toLocaleString('tr-TR'), label: 'Ürün' },
          { val: b.events_today, label: 'Event (bugün)' },
          { val: b.bot_sessions_today, label: 'Bot Oturum' },
          { val: b.last_sync_mins === null ? '—' : b.last_sync_mins < 60 ? `${b.last_sync_mins}dk` : b.last_sync_mins < 1440 ? `${Math.floor(b.last_sync_mins / 60)}s` : `${Math.floor(b.last_sync_mins / 1440)}g`, label: 'Son Sync' }
        ].map((item, i) => (
          <div key={i} style={{ textAlign: 'center', padding: '6px', background: colors.bgCard, borderRadius: radius.sm, border: `1px solid ${colors.borderLight}` }}>
            <div style={{ ...ui.num, fontSize: '13px' }}>{item.val}</div>
            <div style={{ color: colors.textMuted }}>{item.label}</div>
          </div>
        ))}
      </div>
      {msg && <div style={{ marginTop: '8px', fontSize: '12px', color: msg.includes('Hata') ? colors.dangerText : colors.textSecondary }}>{msg}</div>}
    </div>
  )
}

function BrandAdminDashboard({ brandId, headers }) {
  const [veri, setVeri] = useState(null)
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState('')
  const [aralik, setAralik] = useState('7g')
  const [populerSekme, setPopulerSekme] = useState('urun')
  const [satisSekme, setSatisSekme] = useState('urun')

  useEffect(() => {
    let iptal = false
    setYukleniyor(true)
    setHata('')
    axios.get(`${API}/admin/brands/${brandId}/dashboard?aralik=${aralik}`, { headers })
      .then(r => { if (!iptal) { setVeri(r.data); setYukleniyor(false) } })
      .catch(e => { if (!iptal) { setHata(e.response?.data?.error || 'Veri alınamadı'); setYukleniyor(false) } })
    return () => { iptal = true }
  }, [brandId, aralik]) // eslint-disable-line

  const say = v => Number(v || 0).toLocaleString('tr-TR')
  const chartH = h => (typeof window !== 'undefined' && window.innerWidth <= 768 ? 210 : h)

  const huniBos = a => !a || !a.length || a.every(x => !Number(x.deger))

  const huniSecenek = (adimlar, monoton) => {
    if (monoton) {
      return {
        color: chartPalette,
        textStyle: chartBase.textStyle,
        tooltip: { ...chartBase.tooltip, trigger: 'item', formatter: p => `${p.name}<br/>${say(p.value)}` },
        series: [{
          type: 'funnel', left: '4%', right: '4%', top: 8, bottom: 8, minSize: '22%', sort: 'none', gap: 3,
          label: { position: 'inside', color: colors.textOnPrimary, fontSize: 12, fontWeight: 500, formatter: p => `${p.name}  ${say(p.value)}` },
          itemStyle: { borderWidth: 0 },
          data: adimlar.map((a, i) => ({ name: a.ad, value: a.deger, itemStyle: { color: tone(i) } }))
        }]
      }
    }
    const ters = adimlar.slice().reverse()
    return {
      color: chartPalette,
      textStyle: chartBase.textStyle,
      grid: { left: 8, right: 40, top: 8, bottom: 8, containLabel: true },
      tooltip: { ...chartBase.tooltip, trigger: 'item', formatter: p => `${p.name}<br/>${say(p.value)}` },
      xAxis: { type: 'value', ...chartBase.yAxisStyle, splitLine: { show: false } },
      yAxis: { type: 'category', data: ters.map(a => a.ad), ...chartBase.xAxisStyle },
      series: [{
        type: 'bar', barMaxWidth: 18,
        itemStyle: { color: lineGradient, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: colors.textSecondary, fontSize: 11, formatter: p => say(p.value) },
        data: ters.map(a => a.deger)
      }]
    }
  }

  const eventHuni = useMemo(
    () => (veri && veri.funnelEvent && !huniBos(veri.funnelEvent.adimlar) ? huniSecenek(veri.funnelEvent.adimlar, veri.funnelEvent.monoton) : null),
    [veri] // eslint-disable-line
  )
  const ga4Huni = useMemo(
    () => (veri && veri.funnelGa4 && !huniBos(veri.funnelGa4.adimlar) ? huniSecenek(veri.funnelGa4.adimlar, veri.funnelGa4.monoton) : null),
    [veri] // eslint-disable-line
  )

  const Metrik = ({ etiket, deger, degisim, alt, bilgi, birim, kiyas }) => (
    <div style={ui.statBox}>
      <div style={ui.statLabel}>{etiket}</div>
      <div style={{ ...ui.statValue, fontSize: '22px', margin: '2px 0 0' }}>{deger}</div>
      {degisim === 'yeni' ? (
        <div style={{ fontSize: '11px', marginTop: '3px', color: colors.successText }}>yeni · önceki dönemde kayıt yok</div>
      ) : typeof degisim === 'number' ? (
        <div style={{ fontSize: '11px', marginTop: '3px', color: degisim > 0 ? colors.successText : degisim < 0 ? colors.dangerText : colors.textMuted }}>
          {degisim > 0 ? '▲' : degisim < 0 ? '▼' : '—'}{' '}
          {birim === 'puan' ? `${Math.abs(degisim).toLocaleString('tr-TR')} puan` : `%${Math.abs(degisim).toLocaleString('tr-TR')}`}
          {' '}{kiyas || veri?.aralik?.kiyas}
        </div>
      ) : <div style={{ fontSize: '11px', marginTop: '3px', color: colors.textMuted }}>{alt || 'kıyas yok'}</div>}
      {bilgi ? <div style={{ fontSize: '11px', marginTop: '2px', color: colors.textMuted }}>{bilgi}</div> : null}
    </div>
  )

  const Liste = ({ satirlar, basliklar, bosMetin }) => (
    <div className="sx-scroll-x">
      <table className="sx-cards" style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>{basliklar[0]}</th>
            <th style={{ ...s.th, textAlign: 'right', whiteSpace: 'nowrap' }}>{basliklar[1]}</th>
          </tr>
        </thead>
        <tbody>
          {satirlar.map((x, i) => (
            <tr key={i}>
              <td style={s.td} className="sx-break" data-label={basliklar[0]}>{x.ad}</td>
              <td style={{ ...s.td, ...ui.num, textAlign: 'right' }} data-label={basliklar[1]}>{say(x.deger)}</td>
            </tr>
          ))}
          {!satirlar.length && (
            <tr><td colSpan={2} style={{ ...s.td, ...ui.emptyText, padding: '16px', textAlign: 'center' }}>{bosMetin || 'Bu dönemde veri yok.'}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )

  if (yukleniyor) return <div style={{ ...style.card, ...ui.emptyText, textAlign: 'center' }}>Yükleniyor...</div>
  if (hata) return <div style={s.alertDanger}>{hata}</div>
  if (!veri) return null

  const o = veri.ozet
  const ga4Var = o.ziyaretci !== null && o.ziyaretci !== undefined
  const sa = veri.smartAnalysis
  const kap = veri.populer.satisKapsami
  const kapsamEksik = !!(kap && kap.toplam > 0 && kap.detayli < kap.toplam * 0.8)
  const sync = dk => {
    if (dk === null || dk === undefined) return '—'
    if (dk < 60) return dk + ' dk önce'
    if (dk < 1440) return Math.floor(dk / 60) + ' saat önce'
    return Math.floor(dk / 1440) + ' gün önce'
  }

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
        <div>
          <h2 style={{ ...s.pageTitle, margin: 0 }}>Dashboard</h2>
          {veri.aralik.baslangic ? (
            <p style={{ ...s.sectionSubtitle, color: colors.textMuted, margin: '2px 0 0' }}>
              {new Date(veri.aralik.baslangic).toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })} — şimdi
            </p>
          ) : null}
        </div>
        <div className="sx-wrap" style={ui.segmented}>
          {[['bugun', 'Bugün'], ['7g', 'Son 7 gün'], ['30g', 'Son 1 ay']].map(([k, ad]) => (
            <button key={k} onClick={() => setAralik(k)} style={ui.segmentedBtn(aralik === k)}>{ad}</button>
          ))}
        </div>
      </div>

      {veri.uyarilar.length ? (
        <div style={{ marginBottom: '16px' }}>
          {veri.uyarilar.map((u, i) => (
            <div key={i} style={{ ...(u.tur === 'hata' ? s.alertDanger : s.alertWarning), marginBottom: '8px' }}>{u.mesaj}</div>
          ))}
        </div>
      ) : null}

      <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: ga4Var ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', gap: '12px', marginBottom: '16px' }}>
        {ga4Var ? (
          <Metrik etiket="Ziyaretçi" deger={say(o.ziyaretci)} degisim={o.ziyaretciDegisim} kiyas="önceki eşit döneme göre" bilgi={`GA4 · ${o.ziyaretciDonem}`} />
        ) : null}
        <Metrik etiket="Giriş yapan üye" deger={say(o.girisYapanUye)} degisim={o.girisYapanUyeDegisim}
          bilgi={o.uyeOrani !== null && o.uyeOrani !== undefined ? `günlük ortalamada ziyaretçilerin %${o.uyeOrani.toLocaleString('tr-TR')}'si` : null} />
        <Metrik etiket="Yeni üye" deger={say(o.yeniUye)} degisim={o.yeniUyeDegisim} bilgi="ilk kez görülen" />
        <Metrik etiket="Sipariş" deger={say(o.satinAlma)} degisim={o.satinAlmaDegisim}
          kiyas={o.satinAlmaKaynak === 'ga4' ? 'önceki eşit döneme göre' : null}
          bilgi={o.satinAlmaKaynak === 'ga4' ? `GA4 · ${o.ziyaretciDonem}` : 'üye siparişi'} />
        <Metrik etiket="Arama" deger={say(o.arama)} degisim={o.aramaDegisim} bilgi="arama yapan oturum" />
        {o.donusum !== null && o.donusum !== undefined ? (
          <Metrik etiket="Dönüşüm" deger={'%' + o.donusum.toLocaleString('tr-TR')} degisim={o.donusumDegisim} birim="puan"
            kiyas="önceki eşit döneme göre" bilgi={`GA4 · ${o.donusumDonem}`} />
        ) : null}
      </div>

      <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: veri.funnelGa4 ? '1fr 1fr' : '1fr', gap: '16px', marginBottom: '16px' }}>
        <div style={{ ...style.card, minWidth: 0 }}>
          <h3 style={{ ...s.sectionTitle, margin: '0 0 2px' }}>Smart Event hunisi</h3>
          <p style={{ ...s.sectionSubtitle, color: colors.textMuted, margin: '0 0 12px' }}>
            canlı · üye ziyaretçi bazlı
          </p>
          {eventHuni ? <Chart option={eventHuni} height={chartH(240)} /> : <p style={ui.emptyText}>Bu dönemde huni verisi yok.</p>}
        </div>
        {veri.funnelGa4 ? (
          <div style={{ ...style.card, minWidth: 0 }}>
            <h3 style={{ ...s.sectionTitle, margin: '0 0 2px' }}>Analytics hunisi</h3>
            <p style={{ ...s.sectionSubtitle, color: colors.textMuted, margin: '0 0 12px' }}>
              GA4 · {veri.funnelGa4.donem}
            </p>
            {ga4Huni ? <Chart option={ga4Huni} height={chartH(240)} /> : <p style={ui.emptyText}>Bu dönemde huni verisi yok.</p>}
          </div>
        ) : null}
      </div>

      <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div style={{ ...style.card, minWidth: 0 }}>
          <h3 style={{ ...s.sectionTitle, margin: '0 0 10px' }}>En çok görüntülenen</h3>
          <div style={{ ...ui.tabBar, marginBottom: '10px' }}>
            <button onClick={() => setPopulerSekme('urun')} style={ui.tab(populerSekme === 'urun')}>Ürün</button>
            <button onClick={() => setPopulerSekme('kategori')} style={ui.tab(populerSekme === 'kategori')}>Kategori</button>
          </div>
          <Liste basliklar={[populerSekme === 'urun' ? 'Ürün' : 'Kategori', 'Görüntüleme']}
            satirlar={populerSekme === 'urun' ? veri.populer.goruntulenenUrun : veri.populer.goruntulenenKategori} />
        </div>
        <div style={{ ...style.card, minWidth: 0 }}>
          <h3 style={{ ...s.sectionTitle, margin: '0 0 10px' }}>En çok satılan</h3>
          <div style={{ ...ui.tabBar, marginBottom: '10px' }}>
            <button onClick={() => setSatisSekme('urun')} style={ui.tab(satisSekme === 'urun')}>Ürün</button>
            <button onClick={() => setSatisSekme('kategori')} style={ui.tab(satisSekme === 'kategori')}>Kategori</button>
          </div>
          <Liste basliklar={[satisSekme === 'urun' ? 'Ürün' : 'Kategori', 'Sipariş']}
            satirlar={satisSekme === 'urun' ? veri.populer.satilanUrun : veri.populer.satilanKategori} />
          {kapsamEksik ? (
            <p style={{ ...s.sectionSubtitle, color: colors.textMuted, margin: '10px 0 0' }}>
              {say(veri.populer.satisKapsami.toplam)} siparişin {say(veri.populer.satisKapsami.toplam - veri.populer.satisKapsami.detayli)}'inde ürün detayı gelmedi
            </p>
          ) : null}
        </div>
      </div>

      <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div style={{ ...style.card, minWidth: 0 }}>
          <h3 style={{ ...s.sectionTitle, margin: '0 0 10px' }}>En çok aranan</h3>
          <Liste basliklar={['Arama', 'Sayı']} satirlar={veri.aramalar.enCok} />
        </div>
        <div style={{ ...style.card, minWidth: 0 }}>
          <h3 style={{ ...s.sectionTitle, margin: '0 0 2px' }}>Sonuç vermeyenler</h3>
          <p style={{ ...s.sectionSubtitle, color: colors.textMuted, margin: '0 0 10px' }}>aranıyor, bulunamıyor</p>
          <Liste basliklar={['Arama', 'Sayı']} satirlar={veri.aramalar.sonucsuz} />
        </div>
      </div>

      <div style={{ ...style.card, marginBottom: '16px' }}>
        <h3 style={{ ...s.sectionTitle, margin: '0 0 2px' }}>Talep gören tükenmiş ürünler</h3>
        <p style={{ ...s.sectionSubtitle, color: colors.textMuted, margin: '0 0 10px' }}>stokta yok ama bu dönemde görüntülendi</p>
        <Liste basliklar={['Ürün', 'Görüntüleme']} satirlar={veri.talepGorenTukenmis} bosMetin="Tükenmiş ürünlere talep görünmüyor." />
      </div>

      {sa ? (
        <div style={{ ...style.card, marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', marginBottom: '12px' }}>
            <h3 style={{ ...s.sectionTitle, margin: 0 }}>Smart Analysis özeti</h3>
            <span style={{ ...ui.chip, fontSize: '11px' }}>{sa.tarih}</span>
          </div>
          {sa.ozet ? (
            <p style={{ fontSize: '13px', lineHeight: 1.6, color: colors.textSecondary, margin: '0 0 14px' }} className="sx-break">{sa.ozet}</p>
          ) : null}
          <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
            {[
              { ad: 'İyiye giden', liste: sa.iyiye, kutu: s.alertSuccess },
              { ad: 'Korunması gereken', liste: sa.korunmasi, kutu: s.alertWarning },
              { ad: 'Acil müdahale', liste: sa.acil, kutu: s.alertDanger }
            ].map((grup, gi) => (
              <div key={gi} style={{ ...grup.kutu, margin: 0 }}>
                <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', opacity: 0.85 }}>{grup.ad}</div>
                {grup.liste.length ? grup.liste.map((m, mi) => (
                  <div key={mi} style={{ marginBottom: mi === grup.liste.length - 1 ? 0 : '10px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{m.baslik}</div>
                    {m.detay ? <div style={{ fontSize: '12px', lineHeight: 1.5, marginTop: '2px', opacity: 0.9 }} className="sx-break">{m.detay}</div> : null}
                    {m.aksiyon ? <div style={{ fontSize: '12px', lineHeight: 1.5, marginTop: '4px', opacity: 0.75 }} className="sx-break">{m.aksiyon}</div> : null}
                  </div>
                )) : <div style={{ fontSize: '12px', opacity: 0.8 }}>Bu başlıkta madde yok.</div>}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={style.card}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', marginBottom: '14px' }}>
          <h3 style={{ ...s.sectionTitle, margin: 0 }}>Katalog ve işlemler</h3>
          <span style={{ ...ui.chip, fontSize: '11px' }}>şu an</span>
        </div>
        <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '16px' }}>
          <div>
            <div style={style.label}>Stok</div>
            <div style={{ fontSize: '13px', lineHeight: 1.9, color: colors.textPrimary }}>
              <div><span style={ui.num}>{say(veri.anlik.stokta)}</span> stokta</div>
              <div><span style={ui.num}>{say(veri.anlik.tukendi)}</span> tükendi</div>
              <div><span style={ui.num}>{say(veri.anlik.kritik)}</span> kritik seviyede <span style={{ color: colors.textMuted }}>(5 ve altı)</span></div>
              <div style={{ color: colors.textMuted }}>{say(veri.anlik.aktif)} aktif ürün</div>
            </div>
          </div>
          <div>
            <div style={style.label}>Senkron</div>
            <div style={{ fontSize: '13px', lineHeight: 1.9, color: colors.textPrimary }}>
              <div>Ürün · {sync(veri.anlik.urunDakika)}</div>
              <div>Kategori · {sync(veri.anlik.kategoriDakika)}</div>
              <div style={{ color: colors.textMuted }}>{say(veri.anlik.kategori)} kategori</div>
            </div>
          </div>
          <div>
            <div style={style.label}>Çalışan işler</div>
            {veri.isler.length ? (
              <div style={{ fontSize: '13px', lineHeight: 1.9 }}>
                {veri.isler.map((is, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={ui.statusDot(is.durum === 'ok')} />
                    <span className="sx-break" style={{ color: colors.textPrimary }}>{is.ad}</span>
                    <span style={{ color: colors.textMuted, fontSize: '12px', whiteSpace: 'nowrap' }}>{sync(is.dakikaOnce)}</span>
                  </div>
                ))}
              </div>
            ) : <p style={ui.emptyText}>Bu markaya özel iş bulunamadı.</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

function SuperAdminDashboard({ headers }) {
  const mobil = useIsMobile()
  const [sys, setSys] = useState(null)
  const [platform, setPlatform] = useState(null)
  const [sysLoading, setSysLoading] = useState(true)
  const [platLoading, setPlatLoading] = useState(true)
  const [mon, setMon] = useState(null)
  const [health, setHealth] = useState(null)
  const refreshRef = useRef(null)
  const healthRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchAll()
    refreshRef.current = setInterval(fetchSys, 15000)
    healthRef.current = setInterval(fetchHealth, 60000)
    return () => { clearInterval(refreshRef.current); clearInterval(healthRef.current) }
  }, []) // eslint-disable-line

  function fetchAll() { fetchSys(); fetchPlatform(); fetchMon(); fetchHealth() }

  function fetchHealth() {
    axios.get(`${API}/admin/system-health`, { headers })
      .then(r => setHealth(r.data))
      .catch(() => {})
  }

  function fetchMon() {
    axios.get(`${API}/admin/system-monitor`, { headers })
      .then(r => setMon(r.data))
      .catch(() => {})
  }

  function fetchSys() {
    axios.get(`${API}/admin/system-stats`, { headers })
      .then(r => { setSys(r.data); setSysLoading(false) })
      .catch(() => setSysLoading(false))
  }

  function fetchPlatform() {
    axios.get(`${API}/admin/platform-stats`, { headers })
      .then(r => { setPlatform(r.data); setPlatLoading(false) })
      .catch(() => setPlatLoading(false))
  }

  return (
    <div>
      <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <h2 style={s.pageTitle}>Super Admin Dashboard</h2>
        <button onClick={fetchAll} style={s.btnSecondary}>Yenile</button>
      </div>

      {!platLoading && platform && (
        <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
          {[
            { label: 'Toplam Marka', val: platform.summary.total_brands, sub: `${platform.summary.active_brands} aktif` },
            { label: 'Toplam Ürün', val: platform.summary.total_products.toLocaleString('tr-TR'), sub: "DB'deki ürün" },
            { label: 'Bugün Event', val: platform.summary.events_today.toLocaleString('tr-TR'), sub: 'Smart Event' },
            { label: 'Bugün Bot', val: platform.summary.bot_sessions_today, sub: 'SmartBot oturumu' },
            { label: 'Canlı Markalar', val: platform.brands.filter(b => b.event_live).length, sub: 'Son 5 dk event var' }
          ].map((item, i) => (
            <div key={i} style={style.card}>
              <div style={style.label}>{item.label}</div>
              <div style={{ ...ui.statValue, margin: 0 }}>{item.val}</div>
              <div style={style.subval}>{item.sub}</div>
            </div>
          ))}
        </div>
      )}

      {health && (() => {
        const ago = ts => { if (!ts) return '—'; const m = Math.floor((Date.now() - new Date(ts).getTime()) / 60000); if (m < 1) return 'az önce'; if (m < 60) return m + ' dk önce'; const h = Math.floor(m / 60); if (h < 24) return h + ' saat önce'; return Math.floor(h / 24) + ' gün önce' }
        const hedefler = health.targets || []
        const dusenler = hedefler.filter(t => t.current?.status === 'down')
        const st = health.current?.status
        const down = dusenler.length > 0 || st === 'down'
        const pillStyle = { ...(down ? chipDanger : ui.chip), fontSize: '13px', fontWeight: '600' }
        const pill = down
          ? { text: dusenler.length > 1 ? `● ${dusenler.length} SERVİS YANIT VERMİYOR` : '● SERVİS YANIT VERMİYOR' }
          : st === 'up' ? { text: hedefler.length > 1 ? '● TÜM SERVİSLER AYAKTA' : '● SİSTEM AYAKTA' }
          : { text: '● WATCHDOG SESSİZ' }
        const lastInc = (health.incidents || []).slice(-1)[0]
        return (
          <div style={{ ...style.card, marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <h3 style={{ ...s.sectionTitle, margin: 0 }}>Sistem Ayakta mı? <span style={{ fontWeight: 400, color: colors.textMuted, fontSize: '12px' }}>(watchdog, 5 dk'da bir)</span></h3>
                <span style={pillStyle}>{pill.text}</span>
              </div>
              <div style={{ display: 'flex', gap: '18px', fontSize: '13px', color: colors.textSecondary, flexWrap: 'wrap' }}>
                <span>Son kontrol: <b>{ago(health.current?.checked_at)}</b>{health.current?.latency_ms != null ? ` (${health.current.latency_ms}ms)` : ''}</span>
                <span>24s uptime: <b style={ui.num}>{health.uptime24h != null ? '%' + health.uptime24h : '—'}</b></span>
                <span>Son kesinti: <b>{lastInc ? `${ago(lastInc.start)}${lastInc.ongoing ? ' (sürüyor)' : ''}` : 'son 24s yok'}</b></span>
              </div>
            </div>
            {hedefler.length > 0 && (
              <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '8px', marginTop: '12px' }}>
                {hedefler.map(t => {
                  const tDown = t.current?.status === 'down'
                  const tStale = !t.current || t.current.status === 'stale'
                  const nokta = tDown ? colors.danger : tStale ? colors.warning : colors.success
                  return (
                    <div key={t.ad} style={{ border: `1px solid ${tDown ? colors.danger : colors.border}`, borderRadius: radius.md, padding: '10px 12px', background: colors.bgCard }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: nokta, flexShrink: 0, display: 'inline-block' }} />
                        <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>{t.etiket}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '3px' }}>{t.konum}</div>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '12px', color: colors.textSecondary }}>
                        <span style={ui.numMuted}>{t.current?.latency_ms != null ? t.current.latency_ms + ' ms' : '—'}</span>
                        <span>24s <b style={ui.num}>{t.uptime24h != null ? '%' + t.uptime24h : '—'}</b></span>
                      </div>
                      {tDown && t.current?.note && (
                        <div className="sx-break" style={{ fontSize: '11px', color: colors.dangerText, marginTop: '5px' }}>{t.current.note}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
            {(health.strip || []).length > 0 && (
              <div className="sx-wrap" style={{ display: 'flex', gap: '2px', marginTop: '12px', alignItems: 'flex-end', flexWrap: 'wrap', maxWidth: '100%' }}>
                {health.strip.map((c, i) => (
                  <div key={i} title={new Date(c.t).toLocaleTimeString('tr-TR')} style={{ flex: '1 1 3px', height: '14px', borderRadius: '2px', background: c.s ? colors.primary : colors.danger, maxWidth: '12px' }} />
                ))}
                <span style={{ fontSize: '10px', color: colors.textMuted, marginLeft: '8px', whiteSpace: 'nowrap' }}>backend — son {health.strip.length} kontrol</span>
              </div>
            )}
            {health.stale && (
              <div style={{ ...s.alertWarning, marginTop: '10px', padding: '8px 12px', fontSize: '12px' }}>
                Watchdog 12 dakikadan uzun süredir veri yazmıyor — VPS'te "pm2 status seux-watchdog" kontrol edilmeli.
              </div>
            )}
          </div>
        )
      })()}

      {mon && (() => {
        const ago = ts => { if (!ts) return '—'; const m = Math.floor((Date.now() - new Date(ts).getTime()) / 60000); if (m < 1) return 'az önce'; if (m < 60) return m + ' dk önce'; const h = Math.floor(m / 60); if (h < 24) return h + ' saat önce'; return Math.floor(h / 24) + ' gün önce' }
        const errCount = (mon.errors || []).filter(e => Date.now() - new Date(e.finished_at).getTime() < 24 * 3600 * 1000).length
        const stale = (mon.sync || []).filter(x => !x.last_synced || Date.now() - new Date(x.last_synced).getTime() > 26 * 3600 * 1000)
        const lastMail = (mon.mailing || [])[0]
        const jobs = mon.jobs || []
        return (
          <div style={{ ...style.card, marginBottom: '20px' }}>
            <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
              <h3 style={{ ...s.sectionTitle, margin: 0 }}>Sistem Monitörü — Firma Hareketleri</h3>
              <span onClick={() => navigate('/system-monitor')} style={{ cursor: 'pointer', color: colors.primary, fontSize: '13px', fontWeight: 600 }}>Detay →</span>
            </div>
            <div className="sx-grid-stat" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              {[
                { label: 'Son 24s hata', val: errCount },
                { label: 'Bayat senkron', val: stale.length, sub: '26s+ geçen marka' },
                { label: 'Son mailing', val: lastMail ? lastMail.scenario : '—', sub: lastMail ? `${lastMail.brand_name} · ${ago(lastMail.last_sent)}` : 'yok', small: true },
                { label: 'Son job kayıtları', val: jobs.length }
              ].map((item, i) => (
                <div key={i} style={{ background: colors.bgPage, borderRadius: radius.md, padding: '12px 14px' }}>
                  <div style={{ ...ui.statValue, fontSize: item.small ? '15px' : '20px', margin: 0 }}>{item.val}</div>
                  <div style={ui.statLabel}>{item.label}</div>
                  {item.sub && <div style={{ fontSize: '11px', color: colors.textMuted }}>{item.sub}</div>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {jobs.slice(0, 6).map((j, i) => (
                <div key={i} className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', padding: '8px 12px', background: colors.bgPage, borderRadius: radius.md, fontSize: '13px' }}>
                  <span className="sx-break" style={{ minWidth: 0 }}><b>{j.job_name}</b>{j.brand_name ? <span style={{ color: colors.textMuted }}> · {j.brand_name}</span> : null}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ ...(j.status === 'ok' ? ui.chip : chipDanger), fontSize: '11px', background: j.status === 'ok' ? colors.bgCard : colors.dangerLight }}>{j.status === 'ok' ? 'Başarılı' : 'Hata'}</span>
                    <span style={{ color: colors.textMuted, fontSize: '12px' }}>{ago(j.finished_at)}</span>
                  </span>
                </div>
              ))}
              {jobs.length === 0 && <div style={{ ...ui.emptyText, padding: '8px' }}>Henüz job loglanmadı — bir sonraki cron çalışmasında dolacak.</div>}
            </div>
          </div>
        )
      })()}

      <div className="sx-grid-panel" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={style.card}>
          <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            <h3 style={{ ...s.sectionTitle, margin: 0 }}>Sunucu Sağlığı</h3>
            {sys && <span style={{ fontSize: '11px', color: colors.textMuted }}>{new Date(sys.timestamp).toLocaleTimeString('tr-TR')} — her 15sn</span>}
          </div>
          {sysLoading ? <p>Yükleniyor...</p> : !sys ? <p style={ui.emptyText}>Veri alınamadı.</p> : (
            <>
              <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                <div style={{ textAlign: 'center' }}>
                  {gauge(sys.cpu.usage)}
                  <div style={{ ...ui.statLabel, marginTop: '4px' }}>CPU</div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>{sys.cpu.count} çekirdek</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {gauge(sys.memory.pct)}
                  <div style={{ ...ui.statLabel, marginTop: '4px' }}>RAM</div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>{sys.memory.used} / {sys.memory.total} MB</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {gauge(sys.disk.pct)}
                  <div style={{ ...ui.statLabel, marginTop: '4px' }}>Disk</div>
                  <div style={{ fontSize: '11px', color: colors.textMuted }}>{sys.disk.used} / {sys.disk.total} GB</div>
                </div>
              </div>
              <div style={{ background: colors.bgPage, borderRadius: radius.md, padding: '12px', marginBottom: '14px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textSecondary }}>Sunucu Uptime</span>
                  <span style={ui.num}>{sys.uptime.days}g {sys.uptime.hours}s {sys.uptime.mins}dk</span>
                </div>
              </div>
              {sys.pm2.length > 0 && (
                <>
                  <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: colors.textSecondary }}>PM2 Processes</h4>
                  {sys.pm2.map((p, i) => (
                    <div key={i} className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: '8px 12px', background: colors.bgPage, borderRadius: radius.md, marginBottom: '6px', fontSize: '13px' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: colors.textPrimary }}>{p.name}</span>
                        <span style={{ ...(p.status === 'online' ? ui.chip : chipDanger), marginLeft: '8px', fontSize: '11px', background: p.status === 'online' ? colors.bgCard : colors.dangerLight }}>{p.status}</span>
                      </div>
                      <div className="sx-wrap" style={{ display: 'flex', gap: '12px', color: colors.textSecondary, fontSize: '12px', fontVariantNumeric: 'tabular-nums', flexWrap: 'wrap' }}>
                        <span>{p.memory}MB</span>
                        <span>{p.cpu}%</span>
                        <span>{p.restarts} restart</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div style={style.card}>
          <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>Marka Sağlığı</h3>
          {platLoading ? <p>Yükleniyor...</p> : !platform ? <p style={ui.emptyText}>Veri alınamadı.</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: mobil ? 'none' : '560px', overflowY: 'auto' }}>
              {platform.brands.map(b => (
                <BrandSyncRow key={b.id} b={b} headers={headers} onRefresh={fetchPlatform} />
              ))}
            </div>
          )}
        </div>
      </div>

      {platform && platform.brands.some(b => b.influencer_today.total > 0) && (
        <div style={style.card}>
          <h3 style={{ ...s.sectionTitle, margin: '0 0 16px' }}>Bugünkü Influencer Satışları</h3>
          <div className="sx-scroll-x"><table style={s.table}>
            <thead><tr><th style={s.th}>Marka</th><th style={{ ...s.th, textAlign: 'right' }}>Sipariş</th><th style={{ ...s.th, textAlign: 'right' }}>Satış</th></tr></thead>
            <tbody>
              {platform.brands.filter(b => b.influencer_today.total > 0).map(b => (
                <tr key={b.id}>
                  <td style={{ ...s.td, fontWeight: 600 }}>{b.name}</td>
                  <td style={{ ...s.td, ...ui.numMuted, textAlign: 'right' }}>{b.influencer_today.total}</td>
                  <td style={{ ...s.td, ...ui.num, textAlign: 'right' }}>{b.influencer_today.sales.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  )
}

function Dashboard() {
  const { account, selectedBrand } = useBrand()
  const isSuperAdmin = account?.role === 'super_admin'
  const token = localStorage.getItem('adminToken')
  const headers = { Authorization: token }

  if (isSuperAdmin) return <SuperAdminDashboard headers={headers} />

  if (!selectedBrand) return (
    <div style={style.card}>
      <h2 style={{ ...s.pageTitle, margin: '0 0 8px' }}>Hoş geldiniz</h2>
      <p style={{ color: colors.textSecondary, margin: 0 }}>Sol menüden bir marka seçin veya modüle geçin.</p>
    </div>
  )

  return <BrandAdminDashboard brandId={selectedBrand.id} headers={headers} />
}

export default Dashboard